import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MaterialReactTable } from "material-react-table";
import moment from "moment-timezone";
import { Box, Typography, Button, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { mkConfig, generateCsv, download } from "export-to-csv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

function Events() {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [columns, setColumns] = useState([]);
  const [source, setSource] = useState("RIMES");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 }); // MRT uses pageIndex

  const rimColumns = useMemo(() => [
    { accessorKey: "id", header: "Sl. No", size: 10 },
    { accessorKey: "region", header: "Region" },
    {
      accessorKey: "event_datetime",
      header: "Date and Time",
      Cell: ({ row }) =>
        `${moment.utc(row.original.event_datetime).tz("Asia/Dili").format("DD MMM YYYY hh:mm:ss A")}`,
    },
    {
      accessorKey: "magnitude",
      header: "Magnitude",
      Cell: ({ row }) => `${row.original.magnitude} ${row.original.mag_type}`,
    },
    { accessorKey: "depth", header: "Depth (km)" },
    { accessorKey: "latitude", header: "Latitude" },
    { accessorKey: "longitude", header: "Longitude" },
  ], []);

  const usgsColumns = useMemo(() => [
  { accessorKey: "id", header: "Sl. No", size: 10 },
  { accessorKey: "place", header: "Place" },
  {
    accessorKey: "event_datetime",
    header: "Date and Time (TLT)",
    Cell: ({ row }) =>
      moment.utc(Number(row.original.event_datetime))
        .tz("Asia/Dili")
        .format("DD MMM YYYY hh:mm:ss A"),
  },
  {
    accessorKey: "magnitude",
    header: "Magnitude",
    Cell: ({ row }) => `${row.original.magnitude} ${row.original.mag_type || ""}`.trim(),
  },
  { accessorKey: "depth", header: "Depth (km)" },
  { accessorKey: "latitude", header: "Latitude" },
  { accessorKey: "longitude", header: "Longitude" },
  {
    accessorKey: "url",
    header: "Event Page",
    Cell: ({ cell }) => (
      <a href={cell.getValue()} target="_blank" rel="noopener noreferrer">
        View
      </a>
    ),
  },
], []);


  const csvConfig = mkConfig({ fieldSeparator: ",", decimalSeparator: ".", useKeysAsHeaders: true });

  const handleExportData = async () => {
    let url =
      source === "RIMES"
        ? `${backendUrl}EarthQuake/eq_events_all_DP/`
        : `${backendUrl}EarthQuake/eq_events_usgs_all_DP/`;

    const res = await axios.get(url); // full export (careful for huge data)
    const csv = generateCsv(csvConfig)(res.data);
    download(csvConfig)(csv);
  };

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      let url =
        source === "RIMES"
          ? `${backendUrl}EarthQuake/eq_events_all_DP/`
          : `${backendUrl}EarthQuake/eq_events_usgs_all_DP/`;

      // Send pagination params
      const res = await axios.get(url, {
        params: {
          page: pagination.pageIndex + 1, // backend page starts at 1
          page_size: pagination.pageSize,
        },
      });

      // Backend should return { results: [], total: N }
      const { results, total } = res.data;
      setEvents(results);
      setRowCount(total);

    if (source === "RIMES") {
        setColumns(rimColumns);
    } else {
        setColumns(usgsColumns);
    }


      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }, [pagination, source, rimColumns, usgsColumns]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return (
    <div className="container">
      <MaterialReactTable
        columns={columns}
        data={events}
        manualPagination
        rowCount={rowCount}
        state={{ pagination, isLoading: loading }}
        onPaginationChange={setPagination}
        renderTopToolbarCustomActions={() => (
          <Box p={2} display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" fontWeight="bold">
              Earthquake Events
            </Typography>
            <ToggleButtonGroup
              value={source}
              exclusive
              onChange={(e, val) => val && setSource(val)}
              size="small"
            >
              <ToggleButton value="RIMES">RIMES</ToggleButton>
              <ToggleButton value="USGS">USGS</ToggleButton>
            </ToggleButtonGroup>
            <Button onClick={handleExportData} startIcon={<FileDownloadIcon />}>
              Export All Data
            </Button>
          </Box>
        )}
      />
    </div>
  );
}
export default Events;